// ボスの模型（影絵の関節模型）の芯：ポーズの補間・技の表・印6つ（DEV 専用・2026-11-03）。
//   作者の決定＝動きは絵より先にこの模型で作者に聞く。印6つ（足の接地・重心・軌跡・当たり・拍・スロー）を
//   いつも付け、1つずつ表示を切り替えられる（docs/ledger/production.md 11-03）。
//   本番のゲームからは読まない（boss-mockup-lab.html と答えるページの束だけが読む）。
//   座標＝模型の空間（x は前・y は負が上・床が 0）。ボスは画面の左を向く。

export type Vec = [number, number]
/** 関節の位置（Vec）と角度（度）。rx・ry は体全体のずれ（前へ・上下）。 */
export type Pose = Record<string, Vec | number> & { rx: number; ry: number }

export type PhaseKind = 'rest' | 'tell' | 'hit'
export interface MockupPhase { from: number; to: number; label: string; kind: PhaseKind }

export interface MockupMove {
  id: string
  boss: string
  label: string
  /** 体のつくりの型（body.ts の MOCKUP_BODIES のキー） */
  body: string
  durMs: number
  /** 模型の1単位を何 px で描くか（幅 400 の画面のとき） */
  scale: number
  /** 体の原点の画面 x（幅 400 の画面のとき）と、主人公までの距離（模型の単位） */
  baseX: number
  heroAt: number
  keys: { at: number; pose: Pose }[]
  phases: MockupPhase[]
  /** 当たりの始まり（ms）と、当たっている長さ */
  hits: number[]
  activeMs: number
  /** 当たる所＝体の原点からの [前の端, 奥の端, 上, 下]（rx と一緒に動く） */
  box: [number, number, number, number]
  /** 軌跡を描く先端の名前（型の tip が知っている名前） */
  paths: string[]
  /** 拍の帯に細く出す合図（鳴き声など）。省略可 */
  cues?: { at: number; ms: number }[]
  /** 終わりの手前で薄くする長さ（輪の継ぎ目を見せない） */
  fadeOutMs?: number
  /** 実機の拍との対応の説明（ラボと答えるページに出す） */
  source?: string
  /** キーの代わりに時刻から姿勢を計算する（歩きの周期など・keys は空でよい） */
  poseFn?: (t: number) => Pose
  /** その場で歩かせる時の床の流れ（模型の単位／ms）。床に目盛りを描き、着いた足が目盛りと一緒に流れれば滑っていない */
  floorScrollPerMs?: number
  /** 主人公が技を出す模型（11-04・主人公の致命）。白い棒の代わりに崩れて膝をついたボスを heroAt に置き、
   *  box はボスの体（技の原点からの位置・rx と一緒に動かない）。faceRight＝右を向く（ゲームの主人公の向き）。 */
  target?: 'downed'
  /** 崩れたボスの揺れ（dx＝前後のずれ・lean＝上体の傾き度。＋で主人公の側へ倒れる） */
  targetFn?: (t: number) => { dx: number; lean: number }
  faceRight?: boolean
  /** キーの間のつなぎ方。'linear'＝bakeTracks で細かく焼いたキー用（既定 'ease'） */
  interp?: 'ease' | 'linear'
  /** 部屋の物（照明など）を描く（11-05 先代の姫の灯り）。back＝体より奥・front＝体より手前 */
  stage?: { back?: StageDraw; front?: StageDraw }
  /** 主人公を動かして描く（11-05 先代の姫の戦いの場面）。無ければ heroAt に白い棒を立てる */
  hero?: StageDraw
}
/** 画面の座標：x・y は幅 400 の画面の値に k を掛けた px。at＝模型の空間（技の原点から・rx は含めない）を画面へ */
export interface StageEnv { w: number; h: number; G: number; k: number; s: number; at: (pt: Vec) => Vec }
export type StageDraw = (ctx: CanvasRenderingContext2D, t: number, env: StageEnv) => void

export const MOCKUP_MARKS = ['foot', 'com', 'path', 'hit', 'beat', 'slow'] as const
export type MockupMark = typeof MOCKUP_MARKS[number]
export const MOCKUP_MARK_LABELS: Record<MockupMark, string> = {
  foot: '足の接地', com: '重心', path: '軌跡', hit: '当たり', beat: '拍', slow: 'スロー',
}

/** `?marks=foot,com` の形を読む。null（指定なし）なら6つ全部。空文字なら何も付けない。 */
export function parseMarks(s: string | null | undefined): Set<MockupMark> {
  if (s == null) return new Set(MOCKUP_MARKS)
  return new Set(s.split(',').map(v => v.trim()).filter((v): v is MockupMark => (MOCKUP_MARKS as readonly string[]).includes(v)))
}
export function serializeMarks(marks: ReadonlySet<MockupMark>): string {
  return MOCKUP_MARKS.filter(m => marks.has(m)).join(',')
}

export const easeInOut = (u: number): number => u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2

/** t（ms）の姿勢。キーの間は easeInOut でつなぐ。 */
export function poseAt(move: MockupMove, t: number): Pose {
  if (move.poseFn) return move.poseFn(t)
  const k = move.keys
  let i = 0
  while (i < k.length - 2 && k[i + 1]!.at <= t) i++
  const a = k[i]!, b = k[i + 1] ?? a
  const r = b.at === a.at ? 0 : Math.min(1, Math.max(0, (t - a.at) / (b.at - a.at)))
  const u = move.interp === 'linear' ? r : easeInOut(r)
  const o: Record<string, Vec | number> = {}
  for (const key in a.pose) {
    const va = a.pose[key]!, vb = b.pose[key] ?? va
    o[key] = Array.isArray(va) ? [va[0] + ((vb as Vec)[0] - va[0]) * u, va[1] + ((vb as Vec)[1] - va[1]) * u] : va + ((vb as number) - va) * u
  }
  return o as Pose
}

/** いま当たっている何発目か（0 から）。当たっていなければ -1。 */
export function activeHitAt(move: MockupMove, t: number): number {
  return move.hits.findIndex(h => t >= h && t < h + move.activeMs)
}

export function phaseAt(move: MockupMove, t: number): MockupPhase {
  return move.phases.find(p => t >= p.from && t < p.to) ?? move.phases[move.phases.length - 1]!
}

/** 立ち姿に部分の差を重ねる。 */
export const posePatch = (base: Pose) => (o: Partial<Record<string, Vec | number>>): Pose => ({ ...base, ...o } as Pose)

export const lerpV = (a: Vec, b: Vec, u: number): Vec => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u]

// ---------- 部位ごとの時間の線（11-03・全身で動かす技用）----------
//   部位（腰の位置・ひねり・腕の角度・足先…）ごとに別々の時刻でキーを打ち、細かく焼いて MockupMove.keys にする。
//   ease＝そのキーへ向かう区間のつなぎ（in＝加速して当たる・out＝振り抜いて減速・io・lin）。

export type Ease = 'in' | 'out' | 'io' | 'lin'
export type TrackKey = [at: number, v: number | Vec, ease?: Ease]
export type Tracks = Record<string, TrackKey[]>

const EASE: Record<Ease, (u: number) => number> = {
  in: u => u * u * u, out: u => 1 - Math.pow(1 - u, 2), io: easeInOut, lin: u => u,
}

/** 時間の線の t での値。最初のキーより前・最後のキーより後は端の値のまま。 */
export function trackAt(track: TrackKey[], t: number): number | Vec {
  if (t <= track[0]![0]) return track[0]![1]
  for (let i = 1; i < track.length; i++) {
    const [tb, vb, e] = track[i]!
    if (t > tb) continue
    const [ta, va] = track[i - 1]!
    const u = tb === ta ? 1 : EASE[e ?? 'io']((t - ta) / (tb - ta))
    return Array.isArray(va) ? lerpV(va, vb as Vec, u) : va + ((vb as number) - va) * u
  }
  return track[track.length - 1]![1]
}

/** 時間の線を stepMs ごとに焼いてキーにする（姿勢は build が組み立てる）。 */
export function bakeTracks(tracks: Tracks, durMs: number, build: (v: Record<string, number | Vec>) => Pose, stepMs = 8): { at: number; pose: Pose }[] {
  // 一定間隔に加え、キーの時刻そのものも焼く（当たりの瞬間の形が間引かれて崩れないように）
  const times = new Set<number>()
  for (let t = 0; t < durMs; t += stepMs) times.add(t)
  times.add(durMs)
  for (const k in tracks) for (const [at] of tracks[k]!) if (at >= 0 && at <= durMs) times.add(at)
  return [...times].sort((a, b) => a - b).map(t => {
    const v: Record<string, number | Vec> = {}
    for (const k in tracks) v[k] = trackAt(tracks[k]!, t)
    return { at: t, pose: build(v) }
  })
}
